import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useEffect, useRef, useState } from 'react';

export function useDateSegments(value?: string) {
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const isEditingRef = useRef(false);
  const lastValueRef = useRef<string | undefined>(value);

  // Parse ISO8601 string to populate segments
  useEffect(() => {
    // If the new value is a complete ISO date string (from calendar or external source),
    // always update segments regardless of editing state
    const isCompleteDate = value && /^\d{4}-\d{2}-\d{2}$/.test(value);
    
    // Check if segments are empty but we have a value - this indicates we need to sync
    // (e.g., component was reopened/remounted)
    const segmentsAreEmpty = !year && !month && !day;
    const hasValue = !!value;
    const needsSync = hasValue && segmentsAreEmpty;
    
    // Update if:
    // 1. Value actually changed, OR
    // 2. We have a value but segments are empty (component reopened/remounted), OR
    // 3. It's a complete date (from calendar/external source) and value changed
    const valueChanged = value !== lastValueRef.current;
    const shouldUpdate = valueChanged || needsSync || (isCompleteDate && valueChanged);
    
    if (!shouldUpdate) {
      return;
    }

    // Don't reset segments if user is actively editing (typing/backspacing)
    // UNLESS the new value is a complete date (from calendar selection) or segments are empty
    // This prevents the parent's value prop from resetting incomplete input,
    // but allows calendar selections and reopening to always update the display
    if (isEditingRef.current && !isCompleteDate && !needsSync) {
      return;
    }

    lastValueRef.current = value;

    if (value) {
      try {
        const ntsDate = NonTimezoneSensitiveDate.fromISO8601String(value);
        const newYear = String(ntsDate.getYear());
        const newMonth = String(ntsDate.getMonth()).padStart(2, '0');
        const newDay = String(ntsDate.getDate()).padStart(2, '0');
        
        // Only update if values actually differ to avoid unnecessary re-renders
        if (year !== newYear) setYear(newYear);
        if (month !== newMonth) setMonth(newMonth);
        if (day !== newDay) setDay(newDay);
        
        // Clear editing flag when a complete date is set (from calendar)
        if (isCompleteDate) {
          isEditingRef.current = false;
        }
      } catch {
        // Invalid date string, clear segments
        setYear('');
        setMonth('');
        setDay('');
      }
    } else {
      setYear('');
      setMonth('');
      setDay('');
    }
  }, [value, year, month, day]);

  // Expose methods to control editing state
  const setIsEditing = (editing: boolean) => {
    isEditingRef.current = editing;
  };

  return {
    segments: { year, month, day },
    setSegments: {
      setYear,
      setMonth,
      setDay,
    },
    setIsEditing,
  };
}
